import { create } from 'zustand'
import { PackageParseError } from '@/api/packageParseError'
import { uploadMainPackage, uploadPlatformPackage } from '@/api/packages'
import { getVersion } from '@/api/versions'
import { useAppStore } from '@/store/useAppStore'
import type { AppVersion, MainPackageInfo, Platform, UploadTask } from '@/types'

let seq = 0
const nextId = () => `up_${Date.now()}_${seq++}`

/** 进行中的上传，便于 cancel */
const abortByTask = new Map<string, AbortController>()

interface UploadState {
  tasks: UploadTask[]
  collapsed: boolean
  mainParse: MainPackageInfo | null
  /** 上传响应映射出的草稿（真相） */
  draft: AppVersion | null
  draft_version_id: string | null
  draft_app_id: string | null
  /** parse-result 补齐的展示名 */
  draft_app_name: string | null

  startMain: (
    file: File,
    options?: { app_id?: string; version_id?: string },
  ) => void
  startPlatform: (
    platform: Platform,
    file: File,
    ctx: { app_id: string; version_id: string },
  ) => void
  cancelTask: (id: string) => void
  retryTask: (id: string) => void
  removeTask: (id: string) => void
  setCollapsed: (v: boolean) => void
  setDraftVersionId: (id: string | null) => void
  setDraftAppId: (id: string | null) => void
  clear: () => void
  /** 只丢掉主包任务。没有其它任务时等同 clear；子包继续留在浮层 */
  dropMainTasks: () => void
  /** 没有进行中 / 失败任务时才清空；否则留给右下角浮层继续展示 */
  releaseIfIdle: () => void
}

/** 真实发送进度封顶；接口成功后再到 100%，避免服务端处理时误显示已完成 */
const UPLOAD_PROGRESS_CAP = 90
/** 子包直传 R2：发完后只剩 complete 核对，耗时短 */
const PLATFORM_UPLOAD_PROGRESS_CAP = 99
/** 用最近几秒的字节差估速度，避免单次回调把剩余时间打得很跳 */
const ETA_WINDOW_MS = 5_000
/** 样本跨度太短时不展示，先保持「—」 */
const ETA_MIN_SPAN_MS = 800

type ByteSample = { at: number; loaded: number }
const samplesByTask = new Map<string, ByteSample[]>()

function forgetTransferSamples(id: string) {
  samplesByTask.delete(id)
}

function forgetAllTransferSamples() {
  samplesByTask.clear()
}

function formatSpeed(bytesPerSec: number): string {
  const mb = bytesPerSec / (1024 * 1024)
  if (mb >= 10) return `${Math.round(mb)} MB/s`
  if (mb >= 0.95) return `${mb.toFixed(1)} MB/s`
  const kb = bytesPerSec / 1024
  if (kb >= 10) return `${Math.round(kb)} KB/s`
  if (kb >= 1) return `${kb.toFixed(1)} KB/s`
  return `${Math.max(1, Math.round(bytesPerSec))} B/s`
}

/**
 * 用原始已传字节估速度和剩余秒数（不要用封顶后的 progress）。
 * 样本不够时返回 null，调用方保留上一次估计。
 * 字节已经发完时 remaining_sec 为 0。
 */
function estimateTransfer(
  id: string,
  loaded: number,
  total: number,
  now = Date.now(),
): { speed?: string; remaining_sec: number } | null {
  const cutoff = now - ETA_WINDOW_MS
  const samples = (samplesByTask.get(id) ?? []).filter((sample) => sample.at >= cutoff)
  const last = samples[samples.length - 1]
  if (!last || (loaded > last.loaded && now >= last.at)) {
    samples.push({ at: now, loaded })
  }
  samplesByTask.set(id, samples)

  if (total > 0 && loaded >= total) return { remaining_sec: 0 }

  const first = samples[0]
  const latest = samples[samples.length - 1]
  if (!first || !latest) return null
  const span = latest.at - first.at
  const delta = latest.loaded - first.loaded
  if (span < ETA_MIN_SPAN_MS || delta <= 0 || total <= 0) return null

  const bytesPerSec = (delta / span) * 1000
  return {
    speed: formatSpeed(bytesPerSec),
    remaining_sec: Math.max(1, Math.ceil((total - loaded) / bytesPerSec)),
  }
}

function patchTaskProgress(
  set: (fn: (state: UploadState) => Partial<UploadState>) => void,
  id: string,
  size_mb: number,
  file_size: number,
  progressEvent: { loaded: number; total: number },
  cap = UPLOAD_PROGRESS_CAP,
) {
  // loaded === 0：新一次发送（含鉴权重试），允许从封顶进度回到 0
  if (progressEvent.loaded === 0) {
    forgetTransferSamples(id)
    set((state) => ({
      tasks: state.tasks.map((x) =>
        x.id === id && x.status === 'uploading'
          ? {
              ...x,
              progress: 0,
              loaded_mb: 0,
              speed: undefined,
              remaining_sec: undefined,
            }
          : x,
      ),
    }))
    return
  }

  const denom =
    progressEvent.total > 0 ? progressEvent.total : Math.max(file_size, 1)
  const ratio = Math.min(1, progressEvent.loaded / denom)
  const progress = Math.round(ratio * cap)
  const loaded_mb = size_mb * (progress / 100)
  const eta = estimateTransfer(id, progressEvent.loaded, denom)
  set((state) => ({
    tasks: state.tasks.map((x) => {
      if (x.id !== id || x.status !== 'uploading') return x
      if (progress < x.progress) return x
      return {
        ...x,
        progress,
        loaded_mb,
        ...(eta
          ? {
              remaining_sec: eta.remaining_sec,
              ...(eta.speed ? { speed: eta.speed } : {}),
            }
          : {}),
      }
    }),
  }))
}

function failTask(
  set: (fn: (state: UploadState) => Partial<UploadState>) => void,
  id: string,
  status: UploadTask['status'] = 'failed',
  error_message?: string,
) {
  set((state) => ({
    tasks: state.tasks.map((x) =>
      x.id === id ? { ...x, status, error_message } : x,
    ),
  }))
}

export function uploadErrorMessage(err: unknown): string {
  if (err instanceof Error && err.message.trim()) return err.message.trim()
  if (typeof err === 'string' && err.trim()) return err.trim()
  return ''
}

export const useUploadStore = create<UploadState>((set, get) => ({
  tasks: [],
  collapsed: false,
  mainParse: null,
  draft: null,
  draft_version_id: null,
  draft_app_id: null,
  draft_app_name: null,

  startMain: (file, options = {}) => {
    abortByTask.forEach((c) => c.abort())
    abortByTask.clear()

    const id = nextId()
    const size_mb = Math.max(1, Math.round(file.size / 1024 / 1024)) || 1
    const task: UploadTask = {
      id,
      kind: 'main',
      file_name: file.name,
      size_mb,
      loaded_mb: 0,
      progress: 0,
      status: 'uploading',
      file,
      app_id: options.app_id,
      version_id: options.version_id,
    }
    set({
      tasks: [task],
      mainParse: null,
      draft: null,
      draft_version_id: options.version_id ?? null,
      draft_app_id: options.app_id ?? null,
      draft_app_name: null,
      collapsed: false,
    })

    const ac = new AbortController()
    abortByTask.set(id, ac)

    void (async () => {
      try {
        const result = await uploadMainPackage(file, {
          app_id: options.app_id,
          version_id: options.version_id,
          signal: ac.signal,
          onUploadProgress: (event) => {
            if (ac.signal.aborted) return
            patchTaskProgress(set, id, size_mb, file.size, event)
          },
          onParsingStart: () => {
            if (ac.signal.aborted) return
            set((state) => ({
              tasks: state.tasks.map((x) =>
                x.id === id
                  ? {
                      ...x,
                      status: 'parsing' as const,
                      progress: 100,
                      loaded_mb: size_mb,
                    }
                  : x,
              ),
            }))
          },
        })
        if (ac.signal.aborted) return
        set((state) => ({
          mainParse: result.mainParse,
          draft: {
            ...result.draft,
            main_package: result.mainParse,
          },
          draft_version_id: result.version_id,
          draft_app_id: result.app_id,
          draft_app_name: result.appName ?? null,
          tasks: state.tasks.map((x) =>
            x.id === id
              ? {
                  ...x,
                  status: 'success' as const,
                  progress: 100,
                  loaded_mb: size_mb,
                }
              : x,
          ),
        }))
      } catch (err) {
        if (ac.signal.aborted) return
        failTask(
          set,
          id,
          err instanceof PackageParseError ? 'parse_failed' : 'failed',
          uploadErrorMessage(err),
        )
      } finally {
        abortByTask.delete(id)
        forgetTransferSamples(id)
      }
    })()
  },

  startPlatform: (platform, file, ctx) => {
    const id = nextId()
    const size_mb = Math.max(1, Math.round(file.size / 1024 / 1024)) || 1
    const task: UploadTask = {
      id,
      kind: 'platform',
      platform,
      file_name: file.name,
      size_mb,
      loaded_mb: 0,
      progress: 0,
      status: 'uploading',
      file,
      app_id: ctx.app_id,
      version_id: ctx.version_id,
    }
    set((state) => ({
      tasks: [...state.tasks.filter((x) => x.platform !== platform), task],
    }))

    const ac = new AbortController()
    abortByTask.set(id, ac)

    void (async () => {
      try {
        const uploaded = await uploadPlatformPackage({
          app_id: ctx.app_id,
          version_id: ctx.version_id,
          platform,
          file,
          signal: ac.signal,
          onUploadProgress: (event) => {
            if (ac.signal.aborted) return
            patchTaskProgress(
              set,
              id,
              size_mb,
              file.size,
              event,
              PLATFORM_UPLOAD_PROGRESS_CAP,
            )
          },
        })
        if (ac.signal.aborted) return
        // 先按上传响应落本地：详情刷新失败 / 并发时被旧详情覆盖，都不会退回「未上传」
        useAppStore.getState().upsertPlatformPackage(ctx.app_id, ctx.version_id, {
          platform: uploaded.platform,
          is_historical: false,
          upload_id: uploaded.upload_id || undefined,
          package_url: uploaded.package_url,
          file_name: uploaded.file_name,
          size_mb,
          // complete 已成功；显式标记，避免合并时被旧详情的 pending 覆盖
          parse_status: 'success',
          uploaded_locally: true,
        })
        try {
          const detail = await getVersion(ctx.app_id, ctx.version_id)
          useAppStore.getState().ensureRemoteDraft({
            app_id: ctx.app_id,
            version: detail,
          })
        } catch {
          // 上传已成功；详情刷新失败不挡 UI
        }
        set((state) => ({
          tasks: state.tasks.map((x) =>
            x.id === id
              ? {
                  ...x,
                  status: 'success' as const,
                  progress: 100,
                  loaded_mb: size_mb,
                }
              : x,
          ),
        }))
      } catch (err) {
        if (ac.signal.aborted) return
        failTask(set, id, 'failed', uploadErrorMessage(err))
      } finally {
        abortByTask.delete(id)
        forgetTransferSamples(id)
      }
    })()
  },

  cancelTask: (id) => {
    abortByTask.get(id)?.abort()
    abortByTask.delete(id)
    forgetTransferSamples(id)
    set((state) => ({ tasks: state.tasks.filter((x) => x.id !== id) }))
  },

  retryTask: (id) => {
    const task = get().tasks.find((x) => x.id === id)
    if (!task?.file) return
    if (task.kind === 'main') {
      get().startMain(task.file, {
        app_id: task.app_id ?? get().draft_app_id ?? undefined,
        version_id: task.version_id ?? get().draft_version_id ?? undefined,
      })
      return
    }
    if (task.kind === 'platform' && task.platform) {
      const app_id = task.app_id ?? get().draft_app_id
      const version_id = task.version_id ?? get().draft_version_id
      if (!app_id || !version_id) return
      get().startPlatform(task.platform, task.file, { app_id, version_id })
    }
  },

  removeTask: (id) => {
    abortByTask.get(id)?.abort()
    abortByTask.delete(id)
    forgetTransferSamples(id)
    set((state) => ({ tasks: state.tasks.filter((x) => x.id !== id) }))
  },

  setCollapsed: (v) => set({ collapsed: v }),

  setDraftVersionId: (id) => set({ draft_version_id: id }),

  setDraftAppId: (id) => set({ draft_app_id: id }),

  clear: () => {
    abortByTask.forEach((c) => c.abort())
    abortByTask.clear()
    forgetAllTransferSamples()
    set({
      tasks: [],
      mainParse: null,
      draft: null,
      draft_version_id: null,
      draft_app_id: null,
      draft_app_name: null,
      collapsed: false,
    })
  },

  dropMainTasks: () => {
    for (const task of get().tasks) {
      if (task.kind !== 'main') continue
      abortByTask.get(task.id)?.abort()
      abortByTask.delete(task.id)
      forgetTransferSamples(task.id)
    }
    const rest = get().tasks.filter((x) => x.kind !== 'main')
    if (rest.length === 0) {
      get().clear()
      return
    }
    set({ tasks: rest, mainParse: null, draft: null })
  },

  releaseIfIdle: () => {
    const pending = get().tasks.some(
      (x) =>
        x.status === 'uploading' ||
        x.status === 'parsing' ||
        x.status === 'failed' ||
        x.status === 'parse_failed',
    )
    if (!pending) get().clear()
  },
}))
